import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {AuthService} from "../../../auth/services/auth.service";
import {HeaderService} from "../../../tandem-core/services/header.service";
import {SpendingPlan} from "../../models/spending-plan";
import {SpendingPlanService} from "../../services/spending-plan.service";
import {StatementSection} from "../../../tandem-core/models/statements/statement-section";
import {CurrencyService} from "../../../tandem-core/services/currency.service";
import {DialogService} from "../../../tandem-core/services/dialog.service";
import {TandemUser} from "../../../auth/models/tandem-user";
import {UserService} from "../../../auth/user.service";
import {Subject, Subscription, takeUntil} from "rxjs";
import { TandemDatePipe } from 'src/app/modules/tandem-core/directives/tandem-date.pipe';
import {Chat, CommentPanelComponent} from "../../../tandem-core/components/chat/comment-panel/comment-panel.component";
import {Timestamp} from "firebase/firestore";
import {StatementCategory} from "../../../tandem-core/models/statements/statement-category";
import {StatementItem} from "../../../tandem-core/models/statements/statement-item";
import {NotificationType} from "../../../tandem-core/models/tandem-notification";
import {NotificationService} from "../../../tandem-core/services/notification.service";

@Component({
  selector: 'tandem-view-spending-plan',
  templateUrl: './view-spending-plan.component.html',
  styleUrls: ['./view-spending-plan.component.scss']
})
export class ViewSpendingPlanComponent implements OnInit, OnDestroy {

  userId: string | undefined;
  spendingPlanId: string | null = null;
  spendingPlan: SpendingPlan | undefined;
  expenseLabels: {label: string; amount: number}[] = [];

  coachReview: boolean = false;
  reviewedUserId: string | null = '';
  user: TandemUser | null =null;
  private reviewedUser?: TandemUser;

  private eventSubscription?: Subscription;

  showActionDropdown = false;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private datePipe: TandemDatePipe,
              private dialog: DialogService,
              private auth: AuthService,
              private userService: UserService,
              private spendingPlanService: SpendingPlanService,
              private notificationService: NotificationService,
              private currencyService: CurrencyService,
              private headerService: HeaderService) {
    this.spendingPlanId = this.route.snapshot.paramMap.get('id');
    this.reviewedUserId = this.route.snapshot.paramMap.get('userId');
    this.coachReview = router.url.includes('user-management');
  }

  private destroy$ = new Subject<void>();
  ngOnDestroy(): void {
    this.eventSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.auth.$user.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.userId = user?.id;
      this.user = user;
      if (this.reviewedUserId) {
        this.userService.get(this.reviewedUserId).subscribe(revUser => {
          this.reviewedUser = revUser
          this.headerService.setConfig({
            // title: `${this.getTrackingToolName('plural')} - ${this.spendingPlan?.title}${this.coachReview ? ' - Review' : ''}`,
            // subtitle: this.getTrackingToolDescription(),
            breadcrumbs: [
              {label: 'User Management', route: '/coaching/user-management'},
              {label: this.reviewedUser?.displayName || '', route: `/coaching/user-management/${this.reviewedUserId}`},
              {
                label: `${this.getTrackingToolName('plural')}`,
              }
            ],
            hideHeader: true
          })
        });
      }
      if (user && this.spendingPlanId) {
        this.spendingPlanService.get(this.spendingPlanId).subscribe(cf => {
          this.spendingPlan = cf;
          this.headerService.setTitle(`${this.getTrackingToolName('plural')}${this.coachReview ? ' - Review' : ''}`);
          this.headerService.setTitleRow2(`${this.datePipe.transform(this.spendingPlan.startDate.toDate())} to ${this.datePipe.transform(this.spendingPlan.endDate.toDate())}`);
          this.headerService.setSubtitle(this.getTrackingToolDescription());
          if (!this.reviewedUserId) {
            this.headerService.setBreadcrumbs([
              {
                label: `${this.getTrackingToolName('plural')}`,
                route: 'spending-plans'
              },
              {label: this.spendingPlan.title}
            ]);
            this.headerService.setHideHeader(true);
          }
          if (this.dialog.getPanelComponent()?.instance['chatType'] === 'spending-plan') {
            this.dialog.updateComponentData({chats: this.spendingPlan?.comments ? [...this.spendingPlan.comments] : []});
          } else if (this.dialog.getPanelComponent()?.instance['chatType'] === 'revenue') {
            const statementItem = this.spendingPlan?.revenueSections
              .find(category => category.name ===
                this.dialog.getPanelComponent()?.instance['revenueSection']?.name)?.categories
              .find(statementItemCategory => statementItemCategory.name === this.dialog.getPanelComponent()?.instance['statementCategory']?.name)?.items
              .find(item => item.name === this.dialog.getPanelComponent()?.instance['statementItem']?.name);
            this.dialog.updateComponentData({chats: statementItem?.comments ? statementItem.comments : []});
          } else if (this.dialog.getPanelComponent()?.instance['chatType'] === 'expense') {
            const statementItem = this.spendingPlan?.expenseSections
              .find(category => category.name ===
                this.dialog.getPanelComponent()?.instance['expenseSection']?.name)?.categories
              .find(statementItemCategory => statementItemCategory.name === this.dialog.getPanelComponent()?.instance['statementCategory']?.name)?.items
              .find(item => item.name === this.dialog.getPanelComponent()?.instance['statementItem']?.name);
            this.dialog.updateComponentData({chats: statementItem?.comments ? statementItem.comments : []});
          }
          this.expenseLabels = this.spendingPlan.expenseSections.map(section => {
            return {
              label: section.name,
              amount: this.getTotals(section)
            }
          })
        })
      }
    });
    this.eventSubscription = this.dialog.componentEvents$.subscribe(
      event => this.handleChatEvent(event)
    );
  }

  getSummaryText(sectionType: 'revenue' | 'expense', section: StatementSection, i: number): string {
    if (this.spendingPlan) {
      let total = 0;
      if (sectionType === 'revenue') {
        this.spendingPlan.revenueSections[i].categories.forEach(cat => total += cat.total);
      } else {
        this.spendingPlan.expenseSections[i].categories.forEach(cat => total += cat.total);
      }
      return `Total ${section.name}: ${this.currencyService.formatNumber(total)}`;
    }
    return '';
  }

  onDelete() {
    this.dialog.openConfirmDialog('Delete Spending Plan', 'Are you sure you want to delete this spending plan? This action cannot be undone').afterClosed().subscribe(res => {
      if (res) {
        this.spendingPlanService.delete(this.spendingPlanId!).then(res => {
          this.router.navigate(['spending-plans']);
        })
      }
    })
  }

  onEdit() {
    this.router.navigate([`spending-plans/${this.spendingPlanId}/edit`]);
  }
  public getTitle() {
    if (this.spendingPlan) {
      return `${this.formatDate(this.spendingPlan.startDate.toDate())} to ${this.formatDate(this.spendingPlan.endDate.toDate())}`
    }
    return ''
  }
  private formatDate(date: Date) {
    let day: string | number = date.getDate();
    let month: string | number = date.getMonth() + 1; // January is 0!
    let year = date.getFullYear();

    // ensure double digits
    day = day < 10 ? '0' + day : day;
    month = month < 10 ? '0' + month : month;

    return month + '/' + day + '/' + year;
  }

  public routeBack(): void {
    if (this.coachReview) {
      this.router.navigate([`/coaching/user-management/${this.reviewedUserId}`], {queryParams: {tab: 'spendingPlans'}})
    } else {
      this.router.navigate(['/spending-plans']);
    }
  }

  getTrackingToolName(variant: 'single' | 'plural'): string {
    if (variant === 'single') {
      return this.user?.customTTMetadata?.spendingPlanName ? this.user.customTTMetadata.spendingPlanName : 'Spending Plan';
    } else {
      return this.user?.customTTMetadata?.spendingPlanNamePlural ? `${this.user.customTTMetadata.spendingPlanNamePlural}` : 'Spending Plans';
    }
  }

  getTrackingToolDescription() {
    return !!this.user?.customTTMetadata?.spendingPlanDescription ? this.user.customTTMetadata.spendingPlanDescription : 'A prioritized plan for allocating the cash you expect to bring in over a period of time.'
  }

  onComment() {
    const chatData = {
      chats: this.spendingPlan?.comments ? this.spendingPlan.comments : [],
      chatType: 'spending-plan',
      spendingPlan: this.spendingPlan,
      reviewedUserId: this.reviewedUserId
    };
    this.dialog.openSidePanel(CommentPanelComponent, chatData);
  }

  private handleChatEvent(event: any) {
    if (event.type === 'spMessage' && this.spendingPlan) {
      const chat: Chat = {
        message: event.data,
        date: Timestamp.now(),
        status: 'Delivered',
        userId: this.userId || ''
      };

      if (this.spendingPlan?.comments) {
        this.spendingPlan.comments.push(chat);
        this.spendingPlan.comments = [...this.spendingPlan.comments]
      } else {
        this.spendingPlan.comments = [chat];
      }
      this.spendingPlanService.update(this.spendingPlan).then(res => {
          let content = `${this.user?.displayName} has commented on ${this.reviewedUser ? 'your ' : 'their '}${this.getTrackingToolName('single')} from ${this.spendingPlan?.title}`;


          this.notificationService.add({
            type: NotificationType.MentionInComment,
            date: Timestamp.now(),
            userId: !!this.reviewedUserId ? this.reviewedUserId : this.user?.coachId || 'n/a',
            content: content,
            dateModified: Timestamp.now(),
            dateCreated: Timestamp.now(),
            isRead: false,
            sourcePhotoURL: this.user?.photoURL,
            sourceUserId: this.user?.uid,
            sourceUserName: this.user?.displayName,
            link: !!this.reviewedUser ? `spending-plans/${this.spendingPlanId}` : `coaching/user-management/${this.userId}/spending-plans/${this.spendingPlanId}`
          })
      }
      )
    }
  }

  handleRevenueComment($event: { statementSection: StatementSection, statementCategory: StatementCategory; statementItem: StatementItem; comment: Chat }) {
    if (this.spendingPlan) {
      const revenueSection: StatementSection | null = this.spendingPlan?.revenueSections.find(section => section.name === $event.statementSection.name) || null;
      const revenueCategory: StatementCategory | null = revenueSection?.categories.find(category => category.name === $event.statementCategory.name) || null;

      if (revenueCategory) {
        const item = revenueCategory.items.find(item => item.name === $event.statementItem.name) || null;
        if (item) {
          item.comments = item.comments ? [...item.comments, $event.comment] : [$event.comment];
          this.spendingPlanService.update(this.spendingPlan).then(res => {
            let content = `${this.user?.displayName} has commented on ${item.name} for ${this.reviewedUser ? 'your ' : 'their '}${this.getTrackingToolName('single')} from ${this.spendingPlan?.title}`;


            this.notificationService.add({
              type: NotificationType.MentionInComment,
              date: Timestamp.now(),
              userId: !!this.reviewedUserId ? this.reviewedUserId : this.user?.coachId || 'n/a',
              content: content,
              dateModified: Timestamp.now(),
              dateCreated: Timestamp.now(),
              isRead: false,
              sourcePhotoURL: this.user?.photoURL,
              sourceUserId: this.user?.uid,
              sourceUserName: this.user?.displayName,
              link: !!this.reviewedUser ? `spending-plans/${this.spendingPlanId}` : `coaching/user-management/${this.userId}/spending-plans/${this.spendingPlanId}`
            })
          })
        }
      }
    }
  }

  handleExpenseComment($event: { statementSection: StatementSection, statementCategory: StatementCategory; statementItem: StatementItem; comment: Chat }) {
    if (this.spendingPlan) {
      const expenseSection: StatementSection | null = this.spendingPlan?.expenseSections.find(section => section.name === $event.statementSection.name) || null;
      const expenseCategory: StatementCategory | null = expenseSection?.categories.find(category => category.name === $event.statementCategory.name) || null;

      if (expenseCategory) {
        const item = expenseCategory.items.find(item => item.name === $event.statementItem.name) || null;
        if (item) {
          item.comments = item.comments ? [...item.comments, $event.comment] : [$event.comment];
          this.spendingPlanService.update(this.spendingPlan).then(res => {
            let content = `${this.user?.displayName} has commented on ${item.name} for ${this.reviewedUser ? 'your ' : 'their '}${this.getTrackingToolName('single')} from ${this.spendingPlan?.title}`;


            this.notificationService.add({
              type: NotificationType.MentionInComment,
              date: Timestamp.now(),
              userId: !!this.reviewedUserId ? this.reviewedUserId : this.user?.coachId || 'n/a',
              content: content,
              dateModified: Timestamp.now(),
              dateCreated: Timestamp.now(),
              isRead: false,
              sourcePhotoURL: this.user?.photoURL,
              sourceUserId: this.user?.uid,
              sourceUserName: this.user?.displayName,
              link: !!this.reviewedUser ? `spending-plans/${this.spendingPlanId}` : `coaching/user-management/${this.userId}/spending-plans/${this.spendingPlanId}`
            })
          })
        }
      }
    }
  }

  getTotals(section: StatementSection): number {
    let total = 0;
    section.categories.forEach(cat => {
      total += cat.total;
    })

    return total;
  }
}
